import type { EntityPermission } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AclServiceKey, ScopeOption } from '@/shared/utils/permissions';
import { payloadClient } from '.';
import type { FindPayloadApiClientParams } from './types';

export type EntityPermissionQueryParams = FindPayloadApiClientParams<
  'entity-permissions',
  EntityPermission
> & { disabled?: boolean };

export const entityPermissionKeys = {
  all: ['entity-permissions'] as const,
  lists: () => [...entityPermissionKeys.all, 'list'] as const,
  list: (params: EntityPermissionQueryParams) => [...entityPermissionKeys.lists(), params] as const,
  overviews: () => [...entityPermissionKeys.all, 'overview'] as const,
  overview: (params: EntityPermissionOverviewParams) => [
    ...entityPermissionKeys.overviews(),
    params,
  ],
} as const;

export const useEntityPermissions = (params?: EntityPermissionQueryParams) => {
  return useQuery({
    queryKey: entityPermissionKeys.list(params ?? {}),
    enabled: !params?.disabled,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'entity-permissions',
        ...(params as FindPayloadApiClientParams<'entity-permissions', EntityPermission>),
      });
      return response;
    },
  });
};

export type EntityPermissionOverviewEntry = {
  userId: string;
  read: { allowed: boolean; sources: ScopeOption[] };
  update: { allowed: boolean; sources: ScopeOption[] };
};

export type EntityPermissionOverviewResponse = {
  entries: EntityPermissionOverviewEntry[];
};

export type EntityPermissionOverviewParams = {
  entityType: AclServiceKey;
  entityId: string;
  userIds: string[];
  disabled?: boolean;
};

export const useEntityPermissionOverview = (params: EntityPermissionOverviewParams) => {
  return useQuery({
    queryKey: entityPermissionKeys.overview(params),
    enabled: !params.disabled && params.userIds.length > 0,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.customRequest<EntityPermissionOverviewResponse>({
        subpath: '/entity-permissions/overview',
        method: 'POST',
        data: {
          entityType: params.entityType,
          entityId: params.entityId,
          userIds: params.userIds,
        },
      });
      return response;
    },
  });
};

export const useCreateEntityPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<EntityPermission>) => {
      const response = await payloadClient.create({
        collection: 'entity-permissions',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityPermissionKeys.all });
    },
  });
};

export const useUpdateEntityPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EntityPermission> }) => {
      const response = await payloadClient.updateById({
        collection: 'entity-permissions',
        id,
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityPermissionKeys.all });
    },
  });
};

export const useDeleteEntityPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'entity-permissions',
        id,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityPermissionKeys.all });
    },
  });
};
